/* X-All progressive media. Poster-first video: the still always renders; the
   video loads lazily, swaps its source at the declared breakpoint, and never
   plays under prefers-reduced-motion, save-data, or a 2g connection — the
   page works with stills alone (design rule). Ported from the setupVideo
   logic in the design project's Home.jsx.

   Lazy means: nothing is fetched for the video (source or poster) until the
   element is within 200px of the viewport AND the page has finished loading
   (window load, then an idle slot; a 4s safety timer covers pages whose load
   event is held open by third-party scripts). The still is the LCP element,
   so the multi-megabyte video must not compete with it or with the
   render-critical CSS, fonts and scripts. Measured 2026-10-09:
   docs/ops/performance-baseline-20261009.md.

   Markup contract:
   <xall-media>
     …poster <img>/<picture> (class x-pic on the wrapper it should fade)…
     <video class="x-vd" playsinline loop muted preload="none"
       data-src-desktop="…" [data-src-mobile="…"] [data-mq="(max-width:820px)"]
       [data-src-mobile-fallback="…"]
       [data-poster-desktop="…"] [data-poster-mobile="…"]></video>
   data-src-mobile-fallback: played instead if the phone source fails to load
   (the hero derives a 720p phone URL; the fallback is the real 1080p file).
   </xall-media>
   Gets class `x-playing` once the video is actually playing. */
class XallMedia extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video.x-vd');
    if (!this.video) return;
    this.mq = window.matchMedia(this.video.dataset.mq || '(max-width: 820px)');
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.load = this.load.bind(this);
    this.video.addEventListener('playing', () => this.classList.add('x-playing'));
    this.video.addEventListener('error', () => this.fallback());
    this.mq.addEventListener('change', this.load);
    this.reduced.addEventListener('change', this.load);
    this.whenReady(() => {
      this.ready = true;
      this.load();
    });
  }

  disconnectedCallback() {
    if (!this.video) return;
    this.mq.removeEventListener('change', this.load);
    this.reduced.removeEventListener('change', this.load);
    if (this.io) this.io.disconnect();
  }

  whenReady(callback) {
    let done = false;
    const run = () => {
      if (done || !this.isConnected) return;
      done = true;
      callback();
    };
    const afterLoad = () => {
      if (window.requestIdleCallback) window.requestIdleCallback(run, { timeout: 1500 });
      else setTimeout(run, 200);
    };
    const start = () => {
      if (document.readyState === 'complete') afterLoad();
      else window.addEventListener('load', afterLoad, { once: true });
      setTimeout(run, 4000);
    };
    if (!('IntersectionObserver' in window)) {
      start();
      return;
    }
    this.io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.io.disconnect();
        start();
      },
      { rootMargin: '200px 0px' }
    );
    this.io.observe(this);
  }

  load() {
    if (!this.ready) return;
    const v = this.video;
    const phone = this.mq.matches;
    const src = (phone && v.dataset.srcMobile) || v.dataset.srcDesktop;
    const poster = (phone && v.dataset.posterMobile) || v.dataset.posterDesktop;
    if (poster) v.poster = poster;
    const conn = navigator.connection || {};
    if (!src || this.reduced.matches || conn.saveData || /2g/.test(conn.effectiveType || '')) {
      this.classList.remove('x-playing');
      v.removeAttribute('src');
      v.load();
      return;
    }
    if (v.getAttribute('src') === src) return;
    this.start(src);
  }

  start(src) {
    const v = this.video;
    v.muted = true;
    v.setAttribute('src', src);
    v.preload = 'auto';
    v.load();
    v.play().catch(() => {});
  }

  fallback() {
    const v = this.video;
    const failed = v.getAttribute('src');
    const backup = v.dataset.srcMobileFallback;
    if (!failed || !backup || failed !== v.dataset.srcMobile || failed === backup) return;
    this.start(backup);
  }
}

if (!customElements.get('xall-media')) customElements.define('xall-media', XallMedia);
